import { useState } from 'react';
import type { Profile } from '@modelcast/types';
import { LANGUAGES } from '@modelcast/types';
import { Modal } from '../../../components/Modal';

interface ApplicationReviewModalProps {
  profile: Profile;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export function ApplicationReviewModal({ profile, onClose, onApprove, onReject }: ApplicationReviewModalProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isActioning, setIsActioning] = useState(false);

  async function handleApprove() {
    setIsActioning(true);
    await onApprove();
    setIsActioning(false);
  }

  async function handleReject() {
    setIsActioning(true);
    await onReject();
    setIsActioning(false);
  }

  const statusBadge = {
    pending: 'bg-warn/20 text-warn',
    approved: 'bg-ok/20 text-ok',
    rejected: 'bg-red-500/20 text-red-500',
  };

  return (
    <Modal
      onClose={onClose}
      backdropClassName="bg-black/70"
      panelClassName="bg-panel border border-line rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      closeOnBackdrop
      closeOnEscape={false}
      ariaLabel="Review application"
    >
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium text-text">Review Application</h2>
            {profile.applicationStatus && (
              <span className={`px-2 py-0.5 text-[10px] font-semibold uppercaser rounded ${statusBadge[profile.applicationStatus]}`}>
                {profile.applicationStatus}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-text-dim hover:text-text transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Photos */}
            <div className="space-y-6">
              {/* Main photo */}
              <div>
                <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Profile photos</div>
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-panel2 mb-3">
                  {profile.photos[selectedPhotoIndex] ? (
                    <img
                      src={profile.photos[selectedPhotoIndex].url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-faint">
                      No photos
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {profile.photos.length > 1 && (
                  <div className="flex gap-2">
                    {profile.photos.map((photo, index) => (
                      <button
                        key={photo.id}
                        onClick={() => setSelectedPhotoIndex(index)}
                        className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                          index === selectedPhotoIndex
                            ? 'border-accent'
                            : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={photo.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Modeling setup photos</div>
                {profile.spacePhotos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {profile.spacePhotos.map(photo => (
                      <a
                        key={photo.id}
                        href={photo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-[4/3] overflow-hidden rounded-lg border border-line bg-panel2 transition-opacity hover:opacity-85"
                      >
                        <img src={photo.url} alt="" className="h-full w-full object-cover" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-line bg-panel2 px-4 py-6 text-center text-sm text-text-faint">
                    No modeling setup photos
                  </div>
                )}
              </div>
            </div>

            {/* Right: Info */}
            <div className="space-y-5">
              {/* Name & Username */}
              <div>
                <h3 className="text-2xl font-display font-medium text-text">{profile.displayName}</h3>
                {profile.username && (
                  <div className="text-sm text-accent mt-1">
                    <a href={`/u/${profile.username}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      lifedrawing.live/u/{profile.username}
                    </a>
                  </div>
                )}
                {profile.country && (
                  <div className="text-sm text-text-dim mt-1">
                    {new Intl.DisplayNames(['en'], { type: 'region' }).of(profile.country)}
                  </div>
                )}
              </div>

              {/* Links */}
              {(profile.instagram || profile.website) && (
                <div className="flex flex-col gap-2">
                  {profile.instagram && (
                    <a
                      href={`https://instagram.com/${profile.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-accent hover:underline"
                    >
                      @{profile.instagram}
                    </a>
                  )}
                  {profile.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-accent hover:underline truncate"
                    >
                      {profile.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </div>
              )}

              {/* Bio */}
              {profile.bio && (
                <div>
                  <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Bio</div>
                  <p className="text-sm text-text-dim leading-relaxed">{profile.bio}</p>
                </div>
              )}

              {/* Modeling Styles */}
              {profile.modelingStyles && profile.modelingStyles.length > 0 && (
                <div className="pt-4 border-t border-line">
                  <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Modeling Styles</div>
                  <div className="flex flex-wrap gap-2">
                    {profile.modelingStyles.map(style => (
                      <span key={style} className="px-2 py-1 text-xs bg-accent/10 text-accent rounded capitalize">
                        {style.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {profile.languages && profile.languages.length > 0 && (
                <div className="pt-4 border-t border-line">
                  <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Languages</div>
                  <div className="flex flex-wrap gap-2">
                    {profile.languages.map(code => (
                      <span key={code} className="px-2 py-1 text-xs bg-panel2 rounded">
                        {LANGUAGES[code] || code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-line">
                <div>
                  <div className="text-[10px] font-mono text-text-faint uppercaser">Applied</div>
                  <div className="text-sm text-text mt-1">
                    {new Date(profile.createdAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono text-text-faint uppercaser">Photos</div>
                  <div className="text-sm text-text mt-1">
                    {profile.photos.length} profile / {profile.spacePhotos.length} pose area
                  </div>
                </div>
              </div>

              {/* Roles */}
              <div className="pt-4 border-t border-line">
                <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Roles</div>
                <div className="flex flex-wrap gap-2">
                  {profile.isModel && (
                    <span className="px-2 py-1 text-xs bg-panel2 rounded">Model</span>
                  )}
                  {profile.isArtist && (
                    <span className="px-2 py-1 text-xs bg-panel2 rounded">Artist</span>
                  )}
                  {profile.isHost && (
                    <span className="px-2 py-1 text-xs bg-panel2 rounded">Host</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line flex justify-end gap-3 shrink-0 bg-panel">
          <button
            onClick={onClose}
            className="btn"
            disabled={isActioning}
          >
            Cancel
          </button>
          {profile.applicationStatus !== 'rejected' && (
            <button
              onClick={handleReject}
              disabled={isActioning}
              className="btn border-red-500/50 text-red-500 hover:bg-red-500/10"
            >
              {isActioning ? 'Rejecting...' : 'Reject'}
            </button>
          )}
          {profile.applicationStatus !== 'approved' && (
            <button
              onClick={handleApprove}
              disabled={isActioning}
              className="btn btn-accent"
            >
              {isActioning ? 'Approving...' : 'Approve'}
            </button>
          )}
        </div>
    </Modal>
  );
}
